import {formatDate} from '@angular/common';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {flatMap, map} from 'rxjs/operators';
import {Booking} from '../../models/booking';
import {BookingFilter} from '../../models/booking.filter';
import {BaseApiService} from '@core/http/base.api.service';
import {ConfigService} from '@core/services/config.service';
import {PageList} from '@core/models/page-list';

@Injectable({
  providedIn: 'root'
})
export class BookingApiService extends BaseApiService {
  constructor(public http: HttpClient, private configService: ConfigService) {
    super(http);
  }

  private get BaseUrl(): string {
    const config = this.configService.getCurrent();
    return `${config.services.booking}/api/booking`;
  }

  public getById(id: string): Observable<Booking> {
    return super.get<Booking>(`${this.BaseUrl}`, id);
  }

  public getAllBookings(
    search: string,
    filter: BookingFilter,
    pageIndex: number = 0,
    pageOffset: number = 10,
    sort?: string
  ): Observable<PageList<Booking>> {
    const params = new HttpParams()
      .append('search', search)
      .append('from', filter.from ? formatDate(filter.from.toISOString(), 'yyyy-MM-dd', 'en-US') : '')
      .append('to', filter.to ? formatDate(filter.to.toISOString(), 'yyyy-MM-dd', 'en-US') : '')
      .append('userId', filter.userId)
      .append('guideId', filter.guideId)
      .append('experienceId', filter.experienceId)
      .append('status', filter.status)
      .append('includeActive', filter.includeActive ? 'true' : 'false')
      .append('includeHistoric', filter.includeHistoric ? 'true' : 'false')
      .append('includePending', filter.includePending ? 'true' : 'false')
      .append('includeUpcoming', filter.includeUpcoming ? 'true' : 'false')
      .append('includeReviewStatus', filter.includeReviewStatus ? 'true' : 'false')
      .append('pageOffset', pageIndex.toString())
      .append('pageSize', pageOffset.toString())
      .appendAll({'bookingType': filter.bookingType})
      .append('sort', sort ? sort : 'updated desc');

    return super.getAllWithPagination<Booking>(`${this.BaseUrl}`, params);
  }

  public getAllSingleBookings(
    search: string,
    filter: BookingFilter = new BookingFilter(),
    pageIndex: number = 0,
    pageOffset: number = 10,
    sort?: string
  ): Observable<Booking> {
    return this.getAllBookings(search, filter, pageIndex, pageOffset, sort).pipe(
      map((response) => response.items),
      flatMap((res) => res)
    );
  }

  public bookingExperience(booking: Booking): Observable<Booking> {
    return super.register<Booking>(`${this.BaseUrl}`, booking);
  }

  public updateBooking(id: string, booking: Partial<Booking>): Observable<Booking> {
    return super.update<Booking>(`${this.BaseUrl}/${id}`, booking);
  }

  public approveBooking(id: string, booking: Partial<Booking>): Observable<Booking> {
    return super.update<Booking>(`${this.BaseUrl}/${id}/approve`, booking);
  }

  public cancelBooking(id: string, booking: Partial<Booking>): Observable<Booking> {
    return super.update<Booking>(`${this.BaseUrl}/${id}/cancel`, booking);
  }

  public rejectBooking(id: string, booking: Partial<Booking>): Observable<Booking> {
    return super.update<Booking>(`${this.BaseUrl}/${id}/reject`, booking);
  }

  public paidBooking(id: string, booking: Partial<Booking>): Observable<Booking> {
    return super.update<Booking>(`${this.BaseUrl}/${id}/paid`, booking);
  }

  public registerBooking(booking: Partial<Booking>): Observable<Booking> {
    return super.register<Booking>(`${this.BaseUrl}`, booking);
  }

  public registerMultiBooking(bookings: Booking[]): Observable<any> {
    return super.register(`${this.BaseUrl}/multiple`, bookings)
  }

  public checkBooking(userId: string, courseId: string): Observable<boolean> {
    return this.http.get<boolean>(`${this.BaseUrl}/${userId}/Check/${courseId}`);
  }

  public acceptReschedule(id: string): Observable<Booking> {
    return this.http.put<Booking>(`${this.BaseUrl}/${id}/acceptReschedule`, {});
  }

  public refundBooking(id: string): Observable<Booking> {
    return this.http.put<Booking>(`${this.BaseUrl}/${id}/refund`, {});
  }
}
